import { z } from 'zod';
import { IdSchema, TimestampsSchema, paginated } from './common.schema';
import { FieldPriorityConfigSchema } from './client.schema';

// Backend entity: AiAttributeConfig
// Source: /packages/db/prisma/schema.prisma -> model AiAttributeConfig
// TODO: validar lista de modelos soportados con el equipo de IA.

// El back devuelve `categoryId` como número (FK int en Prisma) pero el resto
// del front lo usa como string (HTML select values, URL params). Coercemos en
// el boundary para mantener un único shape interno.
const StringFromNumberOrString = z.preprocess(
  (v) => (typeof v === 'number' ? String(v) : v),
  z.string().nullable().optional(),
);

// JSON Schema parcial que se deep-merge-a sobre el sub-schema canonical antes
// de enviar a Vertex AI como responseSchema. Estructura abierta — keywords
// soportadas: enum, minItems/maxItems, minimum/maximum, minLength/maxLength,
// pattern, properties.*, items.*. Back ya poda keywords no soportadas
// ($ref, oneOf, etc.) antes de mandarlo a Vertex.
const SchemaOverrideShape = z.record(z.string(), z.unknown());

export const AiConfigSchema = z
  .object({
    id: IdSchema,
    attributeName: z.string(),
    categoryId: StringFromNumberOrString,
    promptTemplate: z.string().nullable().optional(),
    model: z.string().default('gemini-2.5-flash'),
    isActive: z.boolean(),
    // Lista de JSON pointers del producto que se envían al LLM.
    // Vacío [] → backend usa default conservador.
    productFields: z.array(z.string()).default([]),
    // Prioridad de fuentes por field. Mismo shape que client.fieldPriorityConfig.
    // null/undefined → backend aplica default { sourcePriority: ['master'], fields: {} }.
    fieldSourcePriority: FieldPriorityConfigSchema.nullable().optional(),
    // Restricciones extra que el admin pone sobre el sub-schema canonical
    // del attributeName (enum, min/maxItems, etc.). null/undefined → no
    // override (solo canonical).
    schemaOverride: SchemaOverrideShape.nullable().optional(),
  })
  .merge(TimestampsSchema);
export type AiConfig = z.infer<typeof AiConfigSchema>;

export const AiConfigListSchema = paginated(AiConfigSchema);
export type AiConfigList = z.infer<typeof AiConfigListSchema>;

// categoryId: el form lo maneja como string (HTML select) pero el backend
// lo espera como integer. Aceptamos ambos y convertimos en el boundary.
const NumericIdInput = z.union([z.string(), z.number()]).optional();

export const AiConfigCreateSchema = z.object({
  attributeName: z.string().min(1).max(80),
  categoryId: NumericIdInput,
  promptTemplate: z.string().min(1).max(8000).optional(),
  // Sin default a proposito: desde el release 2026-09-09 el backend valida
  // `model` contra el catalogo /api/v1/ai-models y rechaza con 400 lo que no
  // este ahi. Un default silencioso nombraria un modelo que puede ya no ser
  // valido, asi que el caller manda el valor del desplegable explicitamente.
  model: z.string().min(1),
  isActive: z.boolean().default(true),
  productFields: z.array(z.string()).max(100).default([]),
  // null borra (vuelve al default backend); undefined no se envía.
  fieldSourcePriority: FieldPriorityConfigSchema.nullable().optional(),
  // Tristate: undefined = preservar BD; null = borrar override (solo
  // canonical); object = override activo. Back deep-merge-a sobre canonical.
  schemaOverride: SchemaOverrideShape.nullable().optional(),
});
export type AiConfigCreate = z.infer<typeof AiConfigCreateSchema>;

export const AiConfigUpdateSchema = AiConfigCreateSchema.partial();
export type AiConfigUpdate = z.infer<typeof AiConfigUpdateSchema>;

// AI config resuelto por producto (back resuelve jerarquía categoría exacta
// → ancestros → global). Endpoint: GET /products/:id/ai-configs.
// Diferente de AiConfig (entidad CRUD) — esta es la VISTA por-producto.
export const ResolvedAiConfigSchema = z.object({
  attributeName: z.string(),
  configId: z.number(),
  categoryId: z.number().nullable(),
  isGlobal: z.boolean(),
  isFromAncestor: z.boolean(),
  model: z.string(),
  hasSchemaOverride: z.boolean(),
});
export type ResolvedAiConfig = z.infer<typeof ResolvedAiConfigSchema>;

export const ResolvedAiConfigsResponseSchema = z.object({
  data: z.array(ResolvedAiConfigSchema),
  // meta.pagination.ancestorChain viaja pero no se usa en UI.
  meta: z.unknown().optional(),
});
export type ResolvedAiConfigsResponse = z.infer<typeof ResolvedAiConfigsResponseSchema>;
